export type SentryEnvironment = 'production' | 'staging' | 'preview' | 'local';

export interface SentryConfig {
    dsn: string;
    environment: SentryEnvironment;
    release: string;
    /** Local environments send nothing unless built with `./run.sh <target> --enableSentry` */
    enabled: boolean;
    /** Tracing is only on for local builds (every trace is sent), deployed environments don't trace */
    tracesSampleRate: number;
}

const SENTRY_DSN = "https://a7f86dbd1a194e6fba2139ec79c7790f@o449271.ingest.sentry.io/5431916";

/**
 * Pull request environments deployed by the CI, e.g. `archipad-js-pr-1234.cloud.archipad.dev`.
 */
const PREVIEW_HOSTNAME_REGEX = /^archipad-js-pr-\d+\./;

/**
 * localhost, `adb reverse` (127.0.0.1) and LAN addresses (testing on a device)
 */
const LOCAL_HOSTNAME_REGEX = /^(localhost|127\.\d+\.\d+\.\d+|\[::1\]|10\.\d+\.\d+\.\d+|192\.168\.\d+\.\d+|172\.(1[6-9]|2\d|3[01])\.\d+\.\d+)$|\.(localhost|local|test)$/;

/**
 * The environment is resolved at runtime from the hostname, so that a production or staging build
 * served locally is reported as `local`, and the preview builds (same build as staging) as `preview`.
 */
export function getSentryEnvironment(isProdBuild: boolean, hostname: string = globalThis.location?.hostname ?? ''): SentryEnvironment {
    if (LOCAL_HOSTNAME_REGEX.test(hostname)) {
        return 'local';
    }
    if (isProdBuild) {
        return 'production';
    }
    if (PREVIEW_HOSTNAME_REGEX.test(hostname)) {
        return 'preview';
    }
    return 'staging';
}

export function getSentryConfig(release: string): SentryConfig {
    const environment = getSentryEnvironment(BUILD_PROD);
    const isSentryEnabledLocally = typeof SENTRY_ENABLE !== 'undefined' && SENTRY_ENABLE === true;

    return {
        dsn: SENTRY_DSN,
        environment,
        release,
        enabled: environment !== 'local' || isSentryEnabledLocally,
        tracesSampleRate: environment === 'local' ? 1 : 0,
    };
}
